/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { CustomerPackage, PackageRecurrence } from '../utils/type';

const SERVICE_ENDPOINT = import.meta.env.VITE_APP_BACKEND_SERVICE;

export interface PurchasePackagePayload {
  serviceId: string;
  tierId: string;
  autoRenew?: boolean;
  /**
   * Payment method surfaced to the backend bookingModel.js enum. The package
   * checkout flow currently presents 'Cash on Hand' (no online payment is
   * captured — purchase is stubbed, see purchasePackage below).
   */
  paymentMethod?: string;
  /**
   * Phase 3.1 — per-session additional-service selection made on
   * PackageSelection (see SessionAddOnSelector). Each entry assigns one
   * additional service to one or more of the tier's session numbers
   * (1-based). Omit entirely (or pass an empty array) when no add-ons were
   * selected — the backend then behaves exactly as it did pre-3.1.
   */
  sessionAddOns?: Array<{
    additionalServiceId: string;
    sessionNumbers: number[];
  }>;
}

export interface BookSessionPayload {
  // BE book-session contract — same shape the normal slot-booking flow sends to
  // createBooking: { serviceId, appointmentDate, appointmentTimeFrom,
  // appointmentTimeTo, staffId, personalInfo, note }.
  providerId: string;
  serviceId: string;
  appointmentDate: string; // YYYY-MM-DD
  appointmentTimeFrom?: string; // e.g. "10:00 AM"
  appointmentTimeTo?: string; // e.g. "10:30 AM"
  staffId?: string;
  personalInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
    };
    bookingNotes?: string;
  };
  note?: string;
  // Phase 3.1 — EXTRA add-ons only (i.e. NOT already included in the
  // package for the session about to be booked). CreditSession.tsx computes
  // this split client-side for display, but the backend
  // (customerPackageService.bookSession) is the source of truth for which
  // ids are "included" vs "extra" — it re-derives the included set itself
  // from the package's sessionAddOns rather than trusting whatever this
  // array contains, so accidentally including an already-included id here
  // is harmless (it gets filtered back out, never double-charged).
  additionalServiceIds?: string[];
}

/** Extract a CustomerPackage from a list/detail response, tolerating envelopes. */
const unwrapPackage = (data: any): CustomerPackage =>
  data?.customerPackage ||
  data?.data?.customerPackage ||
  (data && typeof data === 'object' && 'customerPackageId' in data
    ? data
    : data);

/**
 * POST /v1/customer-package/purchase — customer buys a PackageTier.
 *
 * This call ONLY creates the CustomerPackage record, always with
 * `paymentStatus: 'PENDING'` — it never charges the customer itself and must
 * NOT import or call gigmosaic-payment-service / Stripe directly. Real
 * online payment is a separate step the caller (PackageCheckout.tsx) drives
 * afterward via PaymentSection (paymentType 'package', referenceId the
 * returned customerPackageId); `paymentMethod: 'Cash on Hand'` instead skips
 * that step entirely and leaves the package PENDING (settled offline).
 *
 * `sessionAddOns` (Phase 3.1) is forwarded as-is; the backend validates it
 * against the resolved service/tier (existence, per-session bounds, "at
 * least one session per selected add-on") and rejects with a 400 if invalid
 * — this call never pre-validates it client-side.
 */
export const purchasePackage = async (
  data: PurchasePackagePayload
): Promise<CustomerPackage> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.customerPackagePurchase}`,
    data
  );
  return unwrapPackage(res.data);
};

/**
 * GET /v1/customer-package/customer — my packages (customer).
 */
export const getMyPackages = async (): Promise<CustomerPackage[]> => {
  const res = await apiClient.get(
    `${SERVICE_ENDPOINT}${Path.customerPackageListCustomer}`
  );
  const data = res.data;
  // BE list envelope: { success, message, items, total, page, … } — array lives
  // under `items` (Phase 3 list contract).
  const pkgs =
    data?.items ||
    data?.customerPackages ||
    data?.packages ||
    data?.data ||
    data?.results;
  return Array.isArray(pkgs) ? pkgs : Array.isArray(data) ? data : [];
};

/**
 * POST /v1/customer-package/:id/accept — customer accepts a provider-initiated
 * package offer (offerStatus → 'ACTIVE'). Only meaningful when
 * `initiatedBy === 'provider'` and `offerStatus === 'PENDING_ACCEPTANCE'`.
 */
export const acceptPackageOffer = async (
  customerPackageId: string
): Promise<CustomerPackage> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.customerPackageAccept.replace(
      ':customerPackageId',
      customerPackageId
    )}`
  );
  return unwrapPackage(res.data);
};

/**
 * POST /v1/customer-package/:id/decline — customer declines a provider-initiated
 * package offer (offerStatus → 'DECLINED'). Terminal from the customer's side.
 */
export const declinePackageOffer = async (
  customerPackageId: string
): Promise<CustomerPackage> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.customerPackageDecline.replace(
      ':customerPackageId',
      customerPackageId
    )}`
  );
  return unwrapPackage(res.data);
};

/**
 * POST /v1/customer-package/:id/auto-renew — toggle auto-renewal. Only
 * meaningful for `monthly` recurrence tiers (block packages are single-purchase).
 */
export const toggleAutoRenew = async (
  customerPackageId: string,
  autoRenew: boolean
): Promise<CustomerPackage> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.customerPackageAutoRenew.replace(
      ':customerPackageId',
      customerPackageId
    )}`,
    { autoRenew }
  );
  return unwrapPackage(res.data);
};

/**
 * POST /v1/customer-package/:id/cancel — customer cancels an active package
 * (offerStatus → 'CANCELLED'). Remaining sessions are forfeited. Only callable
 * when offerStatus === 'ACTIVE' and no booking against the package is still
 * scheduled/confirmed.
 */
export const cancelPackage = async (
  customerPackageId: string,
  cancelledReason?: string
): Promise<CustomerPackage> => {
  const body = cancelledReason ? { cancelledReason } : {};
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.customerPackageCancel.replace(
      ':customerPackageId',
      customerPackageId
    )}`,
    body
  );
  return unwrapPackage(res.data);
};

/** Extract a Booking from a book-session response. */
const unwrapBooking = (data: any): any => data?.booking || data?.data?.booking;

export interface BookSessionResult {
  customerPackage: CustomerPackage;
  /**
   * The created PackageSession booking. Phase 4 — carries `bookingId` and
   * `addonSubtotal`; the caller (CreditSession.tsx) uses these to drive the
   * post-booking add-on payment step (PaymentSection, paymentType
   * 'package_session_addon') when addonSubtotal > 0.
   */
  booking: any;
}

/**
 * POST /v1/customer-package/:id/book-session — redeem ONE credit to book a
 * session slot. No payment involved for the credit itself. Any EXTRA add-ons
 * on `data.additionalServiceIds` (Phase 3.1 — ids not already included in
 * the package for this session) ARE charged, either online (a separate
 * PaymentIntent the caller drives afterward via the returned booking's
 * bookingId) or settled directly with the provider if the customer skips it.
 */
export const bookSessionWithPackage = async (
  customerPackageId: string,
  data: BookSessionPayload
): Promise<BookSessionResult> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.customerPackageBookSession.replace(
      ':customerPackageId',
      customerPackageId
    )}`,
    data
  );
  return {
    customerPackage: unwrapPackage(res.data),
    booking: unwrapBooking(res.data),
  };
};

/** Guard: auto-renew switch is only offered for monthly-recurrence tiers. */
export const isRenewablePackage = (pkg: CustomerPackage): boolean =>
  pkg?.tier?.recurrence === ('monthly' as PackageRecurrence);
